import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Star, ArrowRight, CheckCircle, Heart, Award, TrendingUp, Target } from 'lucide-react';
import { siteData } from '../data/mock';

const Home = () => {
  useEffect(() => {
    // Simple scroll animation
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fadeIn');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.animated').forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const iconMap = {
    heart: Heart,
    award: Award,
    'trending-up': TrendingUp,
    ear: Heart,
  };

  return (
    <div className="min-h-screen bg-[var(--bg-page)]">
      {/* Hero Section */}
      <section className="hero-section pt-32 md:pt-40">
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="animated">
              <div className="inline-flex items-center space-x-2 mb-6 bg-white/80 px-4 py-2 rounded-full">
                <Star className="text-yellow-500 fill-yellow-500" size={20} />
                <span className="font-semibold text-[var(--text-primary)]">
                  {siteData.business.rating}/5 ({siteData.business.reviewCount} avis)
                </span>
              </div>
              
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-[var(--text-primary)] leading-tight mb-6">
                Le <span className="text-[var(--brand-primary)]">déclic</span> qui change tout
              </h1>
              
              <p className="text-xl md:text-2xl text-[var(--text-secondary)] mb-8 leading-relaxed">
                Coaching sportif personnalisé & suivi nutritionnel à Lingolsheim
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a
                  href={`tel:${siteData.business.phone.replace(/\s/g, '')}`}
                  className="btn-primary text-center"
                >
                  Appeler maintenant
                </a>
                <Link to="/contact" className="btn-secondary text-center">
                  Prendre rendez-vous
                </Link>
              </div>

              {siteData.business.lgbtqFriendly && (
                <p className="text-[var(--text-secondary)] flex items-center space-x-2">
                  <span>🏳️‍🌈</span>
                  <span className="font-medium">LGBTQ+ friendly</span>
                </p>
              )}
            </div>

            {/* Right Image */}
            <div className="animated relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={siteData.images.hero}
                  alt="Coaching sportif Déclic"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center mb-16 animated">
            <h2 className="text-4xl md:text-5xl font-bold text-[var(--text-primary)] mb-4">
              Un accompagnement complet
            </h2>
            <p className="text-xl text-[var(--text-secondary)] max-w-2xl mx-auto">
              Tu n'as pas besoin d'être parfait, juste de commencer. Déclic t'aide à le faire.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {siteData.services.map((service, index) => (
              <div
                key={service.id}
                className="network-card animated"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="w-12 h-12 rounded-full bg-[var(--brand-primary)] flex items-center justify-center mb-4">
                  <CheckCircle className="text-white" size={24} />
                </div>
                <h3 className="text-2xl font-semibold text-[var(--text-primary)] mb-3">
                  {service.title}
                </h3>
                <p className="text-[var(--text-secondary)] mb-4">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center space-x-2 text-[var(--text-secondary)]">
                      <CheckCircle size={16} className="text-[var(--brand-primary)] flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="text-center mt-12 animated">
            <Link to="/coaching" className="btn-primary inline-flex items-center space-x-2">
              <span>Découvrir tous les services</span>
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-[var(--bg-page)]">
        <div className="container-max">
          <div className="text-center mb-16 animated">
            <h2 className="text-4xl md:text-5xl font-bold text-[var(--text-primary)] mb-4">
              Une méthode bienveillante
            </h2>
            <p className="text-xl text-[var(--text-secondary)] max-w-2xl mx-auto">
              Le plus dur, c'est le premier pas. Déclic t'aide à le faire.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {siteData.values.map((value, index) => {
              const Icon = iconMap[value.icon] || Target;
              return (
                <div
                  key={index}
                  className="text-center animated"
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="w-16 h-16 rounded-full bg-[var(--brand-primary)] flex items-center justify-center mx-auto mb-4">
                    <Icon className="text-white" size={28} />
                  </div>
                  <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-2">
                    {value.title}
                  </h3>
                  <p className="text-[var(--text-secondary)]">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Preview */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center mb-16 animated">
            <h2 className="text-4xl md:text-5xl font-bold text-[var(--text-primary)] mb-4">
              Ils ont trouvé leur déclic
            </h2>
            <p className="text-xl text-[var(--text-secondary)]">
              ⭐ {siteData.business.rating}/5 sur {siteData.business.reviewCount} avis
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {siteData.testimonials.slice(0, 3).map((testimonial, index) => (
              <div
                key={testimonial.id}
                className="network-card animated"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="flex items-center space-x-1 mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={18} className="text-yellow-500 fill-yellow-500" />
                  ))}
                </div>
                <p className="text-[var(--text-secondary)] mb-4 italic">
                  "{testimonial.text}"
                </p>
                <div className="flex justify-between items-center">
                  <p className="font-semibold text-[var(--text-primary)]">
                    {testimonial.name}
                  </p>
                  <p className="text-sm text-[var(--text-light)]">
                    {testimonial.date}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center animated">
            <Link to="/avis" className="btn-primary inline-flex items-center space-x-2">
              <span>Voir tous les avis</span>
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[var(--brand-dark)] text-white">
        <div className="container-max text-center">
          <div className="max-w-3xl mx-auto animated">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Prêt à créer ton déclic ?
            </h2>
            <p className="text-xl mb-8 text-gray-300">
              Commence ton accompagnement personnalisé dès aujourd'hui et transforme ta vie
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/contact" className="btn-primary">
                Prendre rendez-vous
              </Link>
              <a
                href={`tel:${siteData.business.phone.replace(/\s/g, '')}`}
                className="btn-secondary !border-white !text-white hover:!bg-white hover:!text-[var(--brand-dark)]"
              >
                {siteData.business.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
